import { useAuth0 } from '@auth0/auth0-react';
import LoginIcon from '@mui/icons-material/Login';
import StarIcon from '@mui/icons-material/Star';
import { AppBar, Box, Button, Container, Toolbar, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LoadingScreen } from '../components/LoadingScreen';
import { LanguagePicker } from '../components/navigation/LanguagePicker';

/**
 * HomePage - Página de inicio para usuarios tipo Médicos EHR no autenticados
 * Si el usuario está autenticado, redirige automáticamente a /home
 * Si no está autenticado, muestra el navbar con el botón de login
 */
export default function HomePage(): JSX.Element {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    // Si el usuario está autenticado, redirigir a la página principal
    if (isAuthenticated && !isLoading) {
      navigate('/home');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Mientras carga el estado de autenticación
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Usuario no autenticado - mostrar mensaje de bienvenida
  return (
    <>
      {/* Custom Public Header */}
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: '#FFFFFF',
          boxShadow: 'none',
          borderBottom: '1px solid #E0E0E0',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', py: 1 }}>
            <Box
              component="img"
              src="/Logo.png"
              alt="AltonMed Logo"
              sx={{
                height: { xs: 32, md: 40 },
                cursor: 'pointer',
              }}
            />

            {/* Right side - Language picker and Login button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Language Picker with flags */}
              <LanguagePicker />

              <Button
                variant="contained"
                startIcon={<LoginIcon />}
                onClick={() => loginWithRedirect()}
                color="primary"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  px: 2.5,
                  py: 0.75,
                }}
              >
                {t('publicHome.headerLogin')}
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          minHeight: 'calc(100vh - 80px)',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#f9f7f4',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: { xs: 4, md: 8 },
              py: { xs: 4, md: 0 },
            }}
          >
            {/* Left Content */}
            <Box
              sx={{
                flex: 1,
                maxWidth: { xs: '100%', md: '50%' },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  lineHeight: 1.2,
                  mb: 3,
                  color: '#1A1A2E',
                }}
              >
                {t('publicHome.title')}{' '}
                <Box
                  component="span"
                  sx={{
                    color: '#2169F5',
                  }}
                >
                  {t('publicHome.titleHighlight')}
                </Box>
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  color: '#6B7280',
                  lineHeight: 1.7,
                  mb: 4,
                  maxWidth: '500px',
                  mx: { xs: 'auto', md: 0 },
                }}
              >
                {t('publicHome.description')}
              </Typography>

              {/* Excellence Badge */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: '#F0FDF4',
                  borderRadius: '50px',
                  px: 2,
                  py: 1,
                  mb: 4,
                }}
              >
                <Typography
                  sx={{
                    color: '#166534',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  {t('publicHome.badge')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarIcon
                      key={star}
                      sx={{
                        fontSize: '1rem',
                        color: '#FACC15',
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* CTA Button */}
              <Box sx={{ display: 'block' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => loginWithRedirect()}
                  sx={{
                    backgroundColor: '#22222d',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    fontSize: '1rem',
                    textTransform: 'none',
                    borderRadius: '50px',
                    px: 4,
                    py: 1.5,
                    boxShadow: '0 4px 14px rgba(34, 34, 45, 0.3)',
                    '&:hover': {
                      backgroundColor: '#1a1a22',
                      boxShadow: '0 6px 20px rgba(34, 34, 45, 0.4)',
                    },
                  }}
                >
                  {t('publicHome.loginButton')}
                </Button>
              </Box>
            </Box>

            {/* Right Content - Image */}
            <Box
              sx={{
                flex: 1,
                maxWidth: { xs: '100%', md: '45%' },
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '500px',
                  aspectRatio: '4/3',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Box
                  component="img"
                  src="https://medially.co/wp-content/uploads/2024/08/que-es-telemedicina.jpg"
                  alt="Doctor profesional atendiendo consulta de telemedicina"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}
